/**
 * Persist uploaded File/Blob bytes for demand evidence and message attachments
 * so names stay openable after refresh (mirrors paymentProofStore).
 */

const DB_NAME = 'demandjet.uploadBlobs';
const DB_VERSION = 1;
const STORE_NAME = 'files';

/** @typedef {'evidence' | 'message'} UploadBlobKind */

/** In-memory fallback when IndexedDB is unavailable (tests / private mode). */
const memoryStore = new Map();

export const INTAKE_EVIDENCE_OWNER = 'intake:draft';

export function uploadBlobRecordId(kind, ownerId, fileName) {
  return [
    String(kind || '').trim(),
    String(ownerId || '').trim(),
    String(fileName || '').trim(),
  ].join('::');
}

/** Clone to a plain Blob — some browsers fail to persist File handles in IndexedDB. */
function toStoredBlob(file) {
  if (!file) return null;
  if (typeof file.slice === 'function') {
    try {
      return file.slice(0, file.size, file.type || 'application/octet-stream');
    } catch {
      // fall through
    }
  }
  return file;
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined' || !indexedDB) {
      resolve(null);
      return;
    }
    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(err);
      return;
    }
    request.onerror = () => reject(request.error || new Error('IndexedDB open failed'));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('byOwner', ['kind', 'ownerId'], { unique: false });
      }
    };
  });
}

async function putRecord(record) {
  memoryStore.set(record.id, record);
  try {
    const db = await openDb();
    if (!db) return;
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE_NAME).put(record);
    });
  } catch {
    // Memory copy already written for this session.
  }
}

async function getRecord(id) {
  if (memoryStore.has(id)) return memoryStore.get(id);
  try {
    const db = await openDb();
    if (!db) return null;
    const record = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    if (record) memoryStore.set(id, record);
    return record;
  } catch {
    return null;
  }
}

async function deleteRecord(id) {
  memoryStore.delete(id);
  try {
    const db = await openDb();
    if (!db) return;
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE_NAME).delete(id);
    });
  } catch {
    // Memory copy already cleared for this session.
  }
}

/**
 * Store uploaded files under a kind + owner (matter id, intake draft, or message id).
 * @returns {Promise<string[]>} saved file names
 */
export async function putUploadBlobs(kind, ownerId, files) {
  const k = String(kind || '').trim();
  const oid = String(ownerId || '').trim();
  if (!k || !oid) return [];
  const saved = [];
  for (const file of Array.from(files || [])) {
    if (!file) continue;
    const name = String(file.name || '').trim();
    if (!name) continue;
    const blob = toStoredBlob(file);
    if (!blob) continue;
    await putRecord({
      id: uploadBlobRecordId(k, oid, name),
      kind: k,
      ownerId: oid,
      name,
      type: file.type || blob.type || 'application/octet-stream',
      size: Number(file.size) || Number(blob.size) || 0,
      blob,
      createdAt: new Date().toISOString(),
    });
    saved.push(name);
  }
  return saved;
}

export async function getUploadBlobRecord(kind, ownerId, fileName) {
  return getRecord(uploadBlobRecordId(kind, ownerId, fileName));
}

/** Create an object URL for a stored upload, or null when missing. */
export async function resolveUploadBlobUrl(kind, ownerId, fileName) {
  const record = await getUploadBlobRecord(kind, ownerId, fileName);
  if (!record?.blob) return null;
  try {
    return URL.createObjectURL(record.blob);
  } catch {
    return null;
  }
}

/** Remove stored blobs for one owner (optionally filtered by file names). */
export async function deleteUploadBlobs(kind, ownerId, fileNames = []) {
  const k = String(kind || '').trim();
  const oid = String(ownerId || '').trim();
  if (!k || !oid) return 0;

  const names = (fileNames || []).map((name) => String(name || '').trim()).filter(Boolean);
  let removed = 0;

  if (names.length) {
    for (const name of names) {
      await deleteRecord(uploadBlobRecordId(k, oid, name));
      removed += 1;
    }
    return removed;
  }

  const prefix = `${k}::${oid}::`;
  for (const id of [...memoryStore.keys()]) {
    if (String(id).startsWith(prefix)) {
      await deleteRecord(id);
      removed += 1;
    }
  }

  try {
    const db = await openDb();
    if (!db) return removed;
    const rows = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const index = tx.objectStore(STORE_NAME).index('byOwner');
      const req = index.getAll([k, oid]);
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    for (const row of rows) {
      if (!row?.id) continue;
      await deleteRecord(row.id);
      removed += 1;
    }
  } catch {
    // Best-effort cleanup.
  }

  return removed;
}

/** Copy stored blobs from one owner to another (e.g. intake draft → new matter). */
export async function copyUploadBlobs(kind, fromOwnerId, toOwnerId, fileNames = []) {
  const k = String(kind || '').trim();
  const fromId = String(fromOwnerId || '').trim();
  const toId = String(toOwnerId || '').trim();
  if (!k || !fromId || !toId || fromId === toId) return [];

  const names = (fileNames || []).map((name) => String(name || '').trim()).filter(Boolean);
  const copied = [];

  async function copyOne(name) {
    const record = await getUploadBlobRecord(k, fromId, name);
    if (!record?.blob) return;
    await putRecord({
      ...record,
      id: uploadBlobRecordId(k, toId, name),
      ownerId: toId,
      createdAt: new Date().toISOString(),
    });
    copied.push(name);
  }

  if (names.length) {
    for (const name of names) {
      await copyOne(name);
    }
    return copied;
  }

  const prefix = `${k}::${fromId}::`;
  for (const [id, record] of [...memoryStore.entries()]) {
    if (!String(id).startsWith(prefix) || !record?.name) continue;
    await copyOne(record.name);
  }
  return copied;
}

export function createLocalFileObjectUrl(file) {
  if (!file) return null;
  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}

/**
 * True when a stored attachment URL can open in a new tab without a local blob
 * (including signed QuickBooks attachment links served by the API).
 */
export function isOpenableAttachmentUrl(url) {
  return /^(https?:|blob:|data:|\/server\/quickbooks_api\/attachments\/)/i.test(
    String(url || '').trim()
  );
}

/**
 * Best browser-openable URL for an evidence vault reference.
 * HTTP(S)/blob/data pass through; vault / pending-upload schemes fall back to
 * the demo PDF so file names stay clickable when a local blob is missing.
 */
export function evidenceViewerUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return null;
  if (isOpenableAttachmentUrl(raw)) return raw;
  if (/^(catalyst|quickbooks|pending-upload):\/\//i.test(raw)) {
    return '/demo/demand-letter.pdf';
  }
  return null;
}

/** @internal test helper */
export function __resetUploadBlobMemory() {
  memoryStore.clear();
}
